import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

export default function DuplicateTemplateDialog({
  open,
  onOpenChange,
  template,
  sourceVersionId,
  sourceVersionNumber,
  isClinicAdmin,
  activeMode,
  onDuplicateSubmit,
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [ownerType, setOwnerType] = useState('user');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && template) {
      setName(`${template.name} (Copy)`);
      setDescription(template.description || '');
      setSpecialty(template.specialty || '');
      setOwnerType(!['personal', 'everything'].includes(activeMode) && isClinicAdmin ? 'clinic' : 'user');
    }
  }, [open, template, activeMode, isClinicAdmin]);

  if (!template) return null;

  const versionId = sourceVersionId || template.newest_published_version_id;

  const handleSubmit = async e => {
    e.preventDefault();
    if (!name.trim() || submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        specialty: specialty.trim(),
        owner_type: ownerType,
        ...(ownerType === 'clinic' && !['personal', 'everything'].includes(activeMode)
          ? { owner_id: activeMode }
          : {}),
      };
      await onDuplicateSubmit(versionId, payload);
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Duplicate as new template</DialogTitle>
          <DialogDescription>
            Create a new template from {template.name}, Version {sourceVersionNumber || template.newest_published_version_number || '—'}. The source template will not be modified.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="text-sm text-slate-600 bg-slate-50 p-3 rounded border">
            <p className="font-medium">Source: {template.name}</p>
            <p className="text-xs text-slate-500">Version {sourceVersionNumber || template.newest_published_version_number || '—'}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="dup-template-name">New template name</Label>
            <Input
              id="dup-template-name"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="dup-template-desc">Description</Label>
            <Input
              id="dup-template-desc"
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="dup-template-spec">Specialty</Label>
            <Input
              id="dup-template-spec"
              value={specialty}
              onChange={e => setSpecialty(e.target.value)}
            />
          </div>
          {isClinicAdmin && !['personal', 'everything'].includes(activeMode) && (
            <div className="space-y-2">
              <Label htmlFor="dup-owner-type">Owner</Label>
              <select
                id="dup-owner-type"
                className="w-full p-2 border rounded"
                value={ownerType}
                onChange={e => setOwnerType(e.target.value)}
              >
                <option value="user">Personal (Private)</option>
                <option value="clinic">Clinic Template</option>
              </select>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || submitting}>
              {submitting ? 'Duplicating…' : 'Duplicate as new template'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

DuplicateTemplateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  template: PropTypes.object,
  sourceVersionId: PropTypes.string,
  sourceVersionNumber: PropTypes.number,
  isClinicAdmin: PropTypes.bool.isRequired,
  activeMode: PropTypes.string.isRequired,
  onDuplicateSubmit: PropTypes.func.isRequired,
};
